import { Container, Divider } from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { Grid } from "@mui/material";
import axios from "axios";
import {
  ButtonGroupTop,
  CustomTable,
  ModalError,
  ModalSuccess,
  ModalWarning,
  Page,
  PrimaryButton,
  PrimaryDeleteButton,
  PrimaryRestoreButton,
  SearchTextInput,
  SelectInput,
} from "components";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { getErrors } from "utils";
import ModalFormUserComposer from "./Components/ModalFormUserComposer";

function UserPencipta() {
  const classes = globalStyles();
  const isFirstLoad = useRef(true);
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };

  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingFilter, setLoadingFilter] = useState(false);
  const [optionComposer, setOptionComposer] = useState([]);
  const [optionRole, setOptionRole] = useState([]);
  const [dataTable, setDataTable] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    size: 10,
    search: "",
    role_id: "",
    type: "composer",
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const [modalVisible, setModalVisible] = useState(false);
  const [preload, setPreload] = useState(null);
  const [filterParams, setFilterParams] = useState({
    page: 1,
    size: 10,
    search: "",
  });
  const [hasMore, setHasMore] = useState(false);
  const [loadingModalFilter, setLoadingModalFilter] = useState(false);

  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };
  const handleChangeFilterParams = (value, key) => {
    setFilterParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };
  const handleActivate = item => {
    const { is_active_flag, name } = item;
    ModalWarning(
      `Are you sure to ${is_active_flag ? "deactivate" : "restore"} ${name} ?`,
      `${is_active_flag ? "Deactivate" : "Restore"} User Composer`
    ).then(res => res?.isConfirmed && handleActivateUser(item));
  };

  const getOptionRole = async () => {
    try {
      setLoadingFilter(true);
      const res = await axios.get(`${hardBaseUrl}/role?type=composer`, {
        headers,
      });
      const modifiedData = res?.data?.data?.map(item => ({
        ...item,
        id: item?.role_id,
        label: item?.name,
      }));
      setOptionRole(modifiedData);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingFilter(false);
    }
  };
  const getOptionComposer = async () => {
    try {
      setLoadingModalFilter(true);
      const res = await axios.get(`${hardBaseUrl}/composer-option`, {
        headers,
        params: filterParams,
      });
      const { data, meta } = res?.data;
      const newOptions = data?.map(item => ({
        ...item,
        id: item?.composer_id,
      }));
      setOptionComposer(prev => {
        const combined =
          filterParams.page === 1 ? newOptions : [...prev, ...newOptions];
        setHasMore(meta?.total > combined.length);
        return combined;
      });
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingModalFilter(false);
    }
  };
  const debounceOptionsComposer = useCallback(
    debounce(() => {
      getOptionComposer();
    }, 500),
    [filterParams]
  );
  const getDataTable = async () => {
    try {
      const res = await axios.get(`${hardBaseUrl}/user`, {
        headers,
        params: queryParams,
      });
      const { data, meta } = res?.data;
      setDataTable(data || []);
      const pageCount = getTotalPage(meta?.total, queryParams?.size);
      setTableTotalPage(pageCount || 1);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };
  const debounceDataTable = useCallback(
    debounce(async () => {
      setLoadingPage(true);
      getDataTable();
      setLoadingPage(false);
    }, 500),
    [queryParams]
  );
  const fetchData = async () => {
    setLoadingPage(true);
    const promises = [getDataTable()];
    if (isFirstLoad?.current) {
      promises.push(getOptionRole());
      promises.push(getOptionComposer());
    }

    try {
      await Promise.all(promises);
    } catch (error) {
      ModalError(error, "Error fetching data");
    } finally {
      setLoadingPage(false);
      isFirstLoad.current = false;
    }
  };

  const handleActivateUser = async item => {
    const { is_active_flag, user_id } = item;
    try {
      setLoadingPage(true);
      if (is_active_flag) {
        await axios.delete(`${hardBaseUrl}/user/${user_id}`, {
          headers,
        });
      } else {
        await axios.put(
          `${hardBaseUrl}/user-recovery/${user_id}`,
          {},
          { headers }
        );
      }
      ModalSuccess(
        `Successfully ${
          is_active_flag ? "Deactivate" : "Restore"
        } User Composer`
      ).then(() => getDataTable());
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };

  useEffect(() => {
    const isSearching = Boolean(queryParams?.search);
    if (!isFirstLoad?.current) {
      if (isSearching) {
        debounceDataTable();
        return () => {
          debounceDataTable.cancel();
        };
      } else {
        getDataTable();
      }
    } else {
      fetchData();
    }
  }, [queryParams, debounceDataTable]);

  useEffect(() => {
    const isSearching = Boolean(filterParams?.search);
    if (!isFirstLoad?.current) {
      if (isSearching) {
        debounceOptionsComposer();
        return () => {
          debounceOptionsComposer.cancel();
        };
      } else {
        getOptionComposer();
      }
    }
  }, [filterParams, debounceOptionsComposer]);

  return (
    <Page className={classes.root} title="User Composer">
      <Container maxWidth={false}>
        <HeaderTitle title="Parameter" breadcrumbData={breadcrumbData} />
        <ButtonGroupTop />
        <Divider className={classes.flatDivider} />
        <Grid container justifyContent="space-between" my="16px">
          <Grid item>
            <SearchTextInput
              placeholder="Search"
              value={queryParams?.search}
              onChange={e =>
                handleChangeQueryParams(e?.target?.value, "search")
              }
              disabled={loadingPage}
            />
          </Grid>
          <Grid item>
            <Grid container columnSpacing={1} alignItems="center">
              <Grid item>
                <SelectInput
                  label="Role"
                  options={optionRole}
                  optionKey="role_id"
                  optionLabel="name"
                  value={queryParams?.role_id || ""}
                  onChange={e =>
                    handleChangeQueryParams(e?.target?.value, "role_id")
                  }
                  placeholder="All Role"
                  width={200}
                  disabled={loadingPage || loadingFilter}
                />
              </Grid>
              <Grid item>
                <PrimaryButton
                  label="Add User Composer"
                  onClick={() => {
                    setModalVisible(true);
                    setPreload(null);
                  }}
                  startIcon={<Add />}
                  size="large"
                  disabled={loadingPage}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <CustomTable
          isLoading={loadingPage}
          columnTable={columnTable}
          data={dataTable}
          page={queryParams?.page}
          pageSize={queryParams?.size}
          pageCount={tableTotalPage}
          handleChangePage={(_, page) => handleChangeQueryParams(page, "page")}
          handleChangePageSize={e =>
            handleChangeQueryParams(e?.target?.value, "size")
          }
          isHaveAction
          handleEdit={item => {
            setModalVisible(true);
            setPreload(item);
          }}
          customAction={item =>
            item?.is_active_flag ? (
              <PrimaryDeleteButton
                tooltipTitle="Deactivate User Composer"
                onClick={() => handleActivate(item)}
              />
            ) : (
              <PrimaryRestoreButton
                tooltipTitle="Restore User Composer"
                onClick={() => handleActivate(item)}
              />
            )
          }
        />
      </Container>
      {modalVisible && (
        <ModalFormUserComposer
          open={modalVisible}
          onClose={() => setModalVisible(false)}
          preload={preload}
          getData={getDataTable}
          optionComposer={optionComposer}
          optionRole={optionRole}
          filterParams={filterParams}
          handleChangeFilterParams={handleChangeFilterParams}
          hasMore={hasMore}
          setFilterParams={setFilterParams}
          loadingFilter={loadingModalFilter}
        />
      )}
    </Page>
  );
}

const breadcrumbData = [
  {
    label: "Parameter",
    link: "/admin/parameter/user-pencipta",
  },
  {
    label: "User Composer",
    active: true,
  },
];
const columnTable = [
  {
    name: "name",
    title: "User Name",
  },
  {
    name: "role",
    title: "Role",
    renderText: role => role?.name,
  },
  {
    name: "phone",
    title: "Phone Number",
  },
  {
    name: "email",
    title: "Email",
  },
];

export default UserPencipta;
